import { FC, MouseEvent, useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import EventIcon from '@mui/icons-material/Event';
import { IconButton, Popover, Tooltip } from '@mui/material';
import { useSelector } from 'react-redux';
import { greys, mainColors } from '../../../../../styling/theme';
import type { ProspectusRestrictionsTimeSeries } from '../../../../../api/rs-hg/risk/prospectusRestrictions.types';
import { useProspectusRestrictionsTimeSeriesQuery } from '../../../../../api/rs-hg/risk/hooks/queries';
import {
  percentageToTwoDecimalPlacesNoHundred,
  toTwoDecimalPlaces,
} from '../../../../../utilities/numberFormatters';
import {
  getAvailableDateXMonthsAgo,
  getMostRecentAvailableDate,
  getMostRecentWeekday,
  getWeekdayXMonthsAgo,
} from '../../../../../utilities/dateUtilities';
import { availableDatesSelector } from '../../../../../redux/pages/selectors';
import InTableTimeSeries from '../../../../charts/InTableTimeSeries';
import NoTimeSeries from '../../../../charts/NoTimeSeries';
import RaptorLoading from '../../../../feedback/RaptorLoading';
import RaptorDatePicker from '../../../../selects/RaptorDatePicker.component';

type ChartPoint = {
  date: string;
  value: number | null;
  limit: number | null;
  valueDisplay?: string | null;
  limitDisplay?: string | null;
};

type ProspectusRestrictionsTimeSeriesCellProps = {
  fundName: string;
  ruleIndex: number;
  ruleName: string;
  initialTimeSeries: ProspectusRestrictionsTimeSeries | null;
};

function stopRowToggle(event: MouseEvent) {
  event.stopPropagation();
}

function parseNumeric(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') {
    const n = parseFloat(value);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

function parseCell(cellValue: unknown, key: 'value' | 'limit') {
  if (typeof cellValue === 'number') {
    return { n: cellValue, d: null as string | null };
  }
  if (typeof cellValue === 'string') {
    return { n: parseNumeric(cellValue), d: cellValue };
  }
  if (cellValue && typeof cellValue === 'object') {
    const o = cellValue as Record<string, unknown>;
    const raw = o[key];
    return {
      n: parseNumeric(raw),
      d:
        typeof o.display === 'string'
          ? o.display
          : typeof raw === 'string'
            ? raw
            : null,
    };
  }
  return { n: null, d: null };
}

function buildChartFromEmbedded(ts: ProspectusRestrictionsTimeSeries): {
  data: ChartPoint[];
  yAxisTicks?: number[];
  yAxisTickFormatter: (v: number) => string;
  yAxisDomain: any;
  tooltipFormatter: (v: number, name: string, item: any) => string;
} {
  const data = ts.slice(1).map(([date, value, limit]) => {
    const v = parseCell(value, 'value');
    const l = parseCell(limit, 'limit');
    return {
      date: String(date),
      value: v.n,
      limit: l.n,
      valueDisplay: v.d,
      limitDisplay: l.d,
    };
  });

  const labels = new Map<number, string>();
  data.forEach((r) => {
    if (r.value != null && r.valueDisplay) labels.set(r.value, r.valueDisplay);
    if (r.limit != null && r.limitDisplay) labels.set(r.limit, r.limitDisplay);
  });

  const ticks = [...labels.keys()].sort((a, b) => a - b);
  const fmt = (v: number) => labels.get(v) ?? toTwoDecimalPlaces(v);
  const tooltipFormatter = (v: number, name: string, { payload }: any) =>
    (name === 'value' ? payload?.valueDisplay : payload?.limitDisplay) ??
    percentageToTwoDecimalPlacesNoHundred(v);

  if (!ticks.length) {
    return {
      data,
      tooltipFormatter,
      yAxisTickFormatter: fmt,
      yAxisDomain: [
        (min: number) => (min < 0 ? min * 1.1 : min > 0 ? min * 0.1 : 0),
        (max: number) => Math.round(max * 1.1 * 100) / 100,
      ],
    };
  }

  const pad = (ticks[ticks.length - 1] - ticks[0]) * 0.15 || 1;
  return {
    data,
    tooltipFormatter,
    yAxisTicks: ticks,
    yAxisTickFormatter: fmt,
    yAxisDomain: [ticks[0] - pad, ticks[ticks.length - 1] + pad] as [
      number,
      number,
    ],
  };
}

function getEmbeddedDateRange(
  ts: ProspectusRestrictionsTimeSeries | null,
): { startDate: string; endDate: string } | null {
  if (!ts || ts.length < 2) return null;
  const dates = ts
    .slice(1)
    .map((row) => String(row[0]))
    .filter(Boolean)
    .sort();
  if (!dates.length) return null;
  return { startDate: dates[0], endDate: dates[dates.length - 1] };
}

const ProspectusRestrictionsTimeSeriesCell: FC<
  ProspectusRestrictionsTimeSeriesCellProps
> = ({ fundName, ruleIndex, ruleName, initialTimeSeries }) => {
  // Fund position dates from titlebar / get_position_dates (Redux).
  const datesToInclude = useSelector(availableDatesSelector) ?? [];
  const availableDateSet = useMemo(
    () => new Set(datesToInclude),
    [datesToInclude],
  );

  const initialRange = useMemo(
    () => getEmbeddedDateRange(initialTimeSeries),
    [initialTimeSeries],
  );

  const fallbackStartDate = datesToInclude.length
    ? getAvailableDateXMonthsAgo(1, datesToInclude)
    : getWeekdayXMonthsAgo(1);
  const fallbackEndDate = datesToInclude.length
    ? getMostRecentAvailableDate(datesToInclude)
    : getMostRecentWeekday();

  const clampToAvailable = (date: string, fallback: string) =>
    datesToInclude.length === 0 || availableDateSet.has(date)
      ? date
      : fallback;

  const [startDate, setStartDate] = useState(() =>
    clampToAvailable(
      initialRange?.startDate ?? fallbackStartDate,
      fallbackStartDate,
    ),
  );
  const [endDate, setEndDate] = useState(() =>
    clampToAvailable(
      initialRange?.endDate ?? fallbackEndDate,
      fallbackEndDate,
    ),
  );
  const [appliedRange, setAppliedRange] = useState<{
    startDate: string;
    endDate: string;
  } | null>(() =>
    // No usable embedded series (null / single-point dropped upstream) →
    // fetch on mount with the default date range.
    initialTimeSeries
      ? null
      : { startDate: fallbackStartDate, endDate: fallbackEndDate },
  );
  const [calendarAnchor, setCalendarAnchor] = useState<HTMLElement | null>(
    null,
  );

  // Once fund position dates load, keep From/To within that set.
  useEffect(() => {
    if (!datesToInclude.length) return;
    setStartDate((prev) =>
      availableDateSet.has(prev) ? prev : fallbackStartDate,
    );
    setEndDate((prev) =>
      availableDateSet.has(prev) ? prev : fallbackEndDate,
    );
  }, [
    availableDateSet,
    datesToInclude.length,
    fallbackEndDate,
    fallbackStartDate,
  ]);

  const baselineRange = initialRange ?? {
    startDate: fallbackStartDate,
    endDate: fallbackEndDate,
  };

  // What's currently on the chart: a custom applied range, or the embedded baseline.
  const currentAppliedRange = appliedRange ?? baselineRange;

  const rangeDiffersFromBaseline =
    !!appliedRange &&
    (appliedRange.startDate !== baselineRange.startDate ||
      appliedRange.endDate !== baselineRange.endDate);

  const shouldFetch =
    !!appliedRange && (!initialTimeSeries || rangeDiffersFromBaseline);

  // Enable Apply when the draft differs from what's showing, including returning
  // to the original embedded range after a custom fetch.
  const rangeChangedFromApplied =
    startDate !== currentAppliedRange.startDate ||
    endDate !== currentAppliedRange.endDate;

  const canApply =
    !!startDate &&
    !!endDate &&
    startDate <= endDate &&
    !!fundName &&
    !!ruleName &&
    rangeChangedFromApplied &&
    (datesToInclude.length === 0 ||
      (availableDateSet.has(startDate) && availableDateSet.has(endDate)));

  const params = useMemo(
    () =>
      shouldFetch && appliedRange
        ? {
          fundName,
          ruleIndex,
          ruleName,
          startDate: appliedRange.startDate,
          endDate: appliedRange.endDate,
        }
        : null,
    [appliedRange, fundName, ruleIndex, ruleName, shouldFetch],
  );

  const { data, isLoading, isPlaceholderData, isError } =
    useProspectusRestrictionsTimeSeriesQuery(params);

  const embeddedChart = useMemo(
    () =>
      initialTimeSeries ? buildChartFromEmbedded(initialTimeSeries) : null,
    [initialTimeSeries],
  );

  const fetchedChartData = useMemo(
    () =>
      data?.time_series?.map((point) => ({
        date: point.position_date,
        value: point.value,
        limit: point.limit,
      })) ?? [],
    [data],
  );

  const resetDraftDatesToApplied = () => {
    const resetRange = appliedRange ?? baselineRange;
    setStartDate(resetRange.startDate);
    setEndDate(resetRange.endDate);
  };

  const handleOpenCalendar = (event: MouseEvent<HTMLElement>) => {
    stopRowToggle(event);
    // Always open from the applied range so drafts match what's on the chart.
    resetDraftDatesToApplied();
    if (datesToInclude.length) {
      setStartDate((prev) =>
        availableDateSet.has(prev) ? prev : fallbackStartDate,
      );
      setEndDate((prev) =>
        availableDateSet.has(prev) ? prev : fallbackEndDate,
      );
    }
    setCalendarAnchor(event.currentTarget);
  };

  const handleCloseCalendar = () => {
    resetDraftDatesToApplied();
    setCalendarAnchor(null);
  };

  const handleApply = (event: MouseEvent) => {
    stopRowToggle(event);
    if (!canApply) return;
    setAppliedRange({ startDate, endDate });
    setCalendarAnchor(null);
  };

  const rangeLabel = `${startDate} → ${endDate}`;

  const dateControls = (
    <Tooltip title={`Date range: ${rangeLabel}`}>
      <IconButton
        type="button"
        size="small"
        aria-label="Select custom date range"
        onClick={handleOpenCalendar}
      >
        <EventIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  );

  const calendarPopover = (
    <Popover
      open={!!calendarAnchor}
      anchorEl={calendarAnchor}
      onClose={handleCloseCalendar}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      PaperProps={{
        onClick: stopRowToggle,
      }}
    >
      <div className="flex flex-col gap-3 p-3">
        <div className="text-[12px] font-semibold text-gray-700">
          Custom date range
        </div>
        <div className="flex flex-row flex-wrap items-center gap-2">
          <RaptorDatePicker
            title="From:"
            selectedDate={dayjs(startDate)}
            datesToInclude={datesToInclude}
            tiedToActiveDate={false}
            mainRootStyle={{ backgroundColor: greys.grey100 }}
            handler={(val) => {
              if (!val) return;
              setStartDate(dayjs(val).format('YYYY-MM-DD'));
            }}
          />
          <RaptorDatePicker
            title="To:"
            selectedDate={dayjs(endDate)}
            datesToInclude={datesToInclude}
            tiedToActiveDate={false}
            mainRootStyle={{ backgroundColor: greys.grey100 }}
            handler={(val) => {
              if (!val) return;
              setEndDate(dayjs(val).format('YYYY-MM-DD'));
            }}
          />
        </div>
        <div className="flex items-center justify-end gap-1.5">
          <button
            type="button"
            className="rounded border border-gray-300 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-50"
            onClick={(event) => {
              stopRowToggle(event);
              handleCloseCalendar();
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            className="rounded bg-mainblue px-2 py-1 text-[11px] font-semibold text-white hover:bg-mainblue_hover disabled:cursor-not-allowed disabled:opacity-45"
            disabled={!canApply}
            onClick={handleApply}
          >
            Apply
          </button>
        </div>
      </div>
    </Popover>
  );

  const chartLines = [
    { dataKey: 'limit', color: mainColors.Fail_darker },
    { dataKey: 'value', color: mainColors.mainBlue },
  ];

  const renderChartShell = (
    chartData: ChartPoint[],
    options?: {
      idSuffix?: string;
      yAxisTicks?: number[];
      yAxisTickFormatter?: (v: number) => string;
      yAxisDomain?: any;
      formatter?: any;
      showLoadingOverlay?: boolean;
      errorOverlay?: string;
    },
  ) => {
    const shellData =
      chartData.length > 0
        ? chartData
        : [{ date: startDate, value: null, limit: null }];

    return (
      <div className="relative w-full">
        <InTableTimeSeries
          id={`${fundName}-${ruleIndex}-${options?.idSuffix ?? 'chart'}`}
          export
          headerActions={dateControls}
          lines={chartLines}
          error={null}
          title=""
          data={shellData}
          yAxisTicks={options?.yAxisTicks}
          yAxisTickFormatter={options?.yAxisTickFormatter}
          yAxisDomain={options?.yAxisDomain}
          formatter={
            options?.formatter ??
            ((value: number) => percentageToTwoDecimalPlacesNoHundred(value))
          }
        />
        {options?.showLoadingOverlay ? (
          <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center bg-white/70">
            <RaptorLoading
              messages={['Loading time series…']}
              noMargin
              setWidth="2.5rem"
            />
          </div>
        ) : null}
        {options?.errorOverlay ? (
          <div className="pointer-events-none absolute inset-0 z-[1] flex items-center justify-center px-3 text-center">
            <p className="text-[12px] font-semibold text-red-700">
              {options.errorOverlay}
            </p>
          </div>
        ) : null}
      </div>
    );
  };

  const renderFetchedFallbackShell = (options: {
    idSuffix: string;
    showLoadingOverlay?: boolean;
    errorOverlay?: string;
  }) => {
    if (fetchedChartData.length) {
      return renderChartShell(fetchedChartData, {
        idSuffix: `${options.idSuffix}-fetched`,
        showLoadingOverlay: options.showLoadingOverlay,
        errorOverlay: options.errorOverlay,
      });
    }
    if (embeddedChart?.data.length) {
      return renderChartShell(embeddedChart.data, {
        idSuffix: `${options.idSuffix}-embedded`,
        yAxisTicks: embeddedChart.yAxisTicks,
        yAxisTickFormatter: embeddedChart.yAxisTickFormatter,
        yAxisDomain: embeddedChart.yAxisDomain,
        formatter: embeddedChart.tooltipFormatter,
        showLoadingOverlay: options.showLoadingOverlay,
        errorOverlay: options.errorOverlay,
      });
    }
    return renderChartShell([], {
      idSuffix: `${options.idSuffix}-empty`,
      showLoadingOverlay: options.showLoadingOverlay,
      errorOverlay: options.errorOverlay,
    });
  };

  const renderChart = () => {
    // Lagged query: overlay on first load or while the previous range is still
    // on screen. Skip overlay on stale refetches of the same key (isFetching).
    if (isLoading || isPlaceholderData) {
      return renderFetchedFallbackShell({
        idSuffix: 'loading',
        showLoadingOverlay: true,
      });
    }

    if (!shouldFetch) {
      if (!embeddedChart?.data.length) {
        return (
          <div className="flex flex-col items-end gap-1">
            {dateControls}
            <NoTimeSeries />
          </div>
        );
      }
      return renderChartShell(embeddedChart.data, {
        idSuffix: 'initial',
        yAxisTicks: embeddedChart.yAxisTicks,
        yAxisTickFormatter: embeddedChart.yAxisTickFormatter,
        yAxisDomain: embeddedChart.yAxisDomain,
        formatter: embeddedChart.tooltipFormatter,
      });
    }

    if (isError) {
      return renderFetchedFallbackShell({
        idSuffix: 'error',
        errorOverlay:
          'Failed to load time series. Choose a date range and apply to try again.',
      });
    }

    if (!fetchedChartData.length) {
      return renderFetchedFallbackShell({
        idSuffix: 'empty',
        errorOverlay: 'No time series data available',
      });
    }

    return renderChartShell(fetchedChartData, { idSuffix: 'custom' });
  };

  return (
    <div
      className="flex w-full flex-col px-2 py-1"
      onClick={stopRowToggle}
      onMouseDown={stopRowToggle}
    >
      {calendarPopover}
      {renderChart()}
    </div>
  );
};

export default ProspectusRestrictionsTimeSeriesCell;
